import { useMemo, useState } from "react";

import { describeError } from "@/api/client";
import { useCanEdit } from "@/api/session";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  InlineAlert,
  PageHeader,
  Progress,
  RelativeTime,
  Select,
  Toolbar,
  Tooltip,
  useToast,
  VisuallyHidden,
  type BadgeTone,
  type DataTableColumn,
  type IconName,
} from "@/components";
import { formatDuration, formatRelativeTime } from "@/lib/format";
import { useNow, useSearchParamsInPlace } from "@/lib/hooks";
import {
  JOB_STATUSES,
  isJobActive,
  isJobRequeueable,
  useCancelJob,
  useJobKinds,
  useJobs,
  useRequeueJob,
  useWorkerStatus,
  type Job,
  type JobStatus,
} from "./api";
import { EnqueueJobDialog } from "./EnqueueJobDialog";
import { JobDetailsDialog } from "./JobDetailsDialog";
import styles from "./jobs.module.css";

const STATUS_LOOK: Record<JobStatus, { label: string; tone: BadgeTone; icon: IconName }> = {
  queued: { label: "Queued", tone: "neutral", icon: "list" },
  running: { label: "Running", tone: "info", icon: "refresh" },
  done: { label: "Done", tone: "success", icon: "check" },
  failed: { label: "Failed", tone: "danger", icon: "x" },
  cancelled: { label: "Cancelled", tone: "neutral", icon: "stop" },
};

export function JobStatusBadge({ job }: { job: Job }) {
  const look = STATUS_LOOK[job.status];
  const cancelling = job.cancel_requested && isJobActive(job);
  return (
    <Badge tone={look.tone} icon={look.icon}>
      {cancelling ? "Cancelling…" : look.label}
    </Badge>
  );
}

function elapsed(job: Job, now: number): number | null {
  if (!job.started_at) return null;
  const end = job.finished_at ? new Date(job.finished_at).getTime() : now;
  return end - new Date(job.started_at).getTime();
}

function isJobStatus(value: string | null): value is JobStatus {
  return JOB_STATUSES.includes(value as JobStatus);
}

/** Background work: recent jobs with live progress and errors, start allow-listed jobs, cancel queued or
 * running ones, retry failed or cancelled ones. */
export function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const statusParam = searchParams.get("status");
  const status = isJobStatus(statusParam) ? statusParam : undefined;
  const kind = searchParams.get("kind") || undefined;
  const jobs = useJobs({ status, kind });
  const kinds = useJobKinds();
  const worker = useWorkerStatus();
  const cancel = useCancelJob();
  const requeue = useRequeueJob();
  const canEdit = useCanEdit();
  const toast = useToast();
  const [enqueueOpen, setEnqueueOpen] = useState(false);
  const [detailsJob, setDetailsJob] = useState<Job | null>(null);
  const now = useNow(1000);

  const descriptions = useMemo(
    () => new Map((kinds.data ?? []).map((item) => [item.kind, item.description])),
    [kinds.data],
  );
  const startableKinds = useMemo(() => new Set((kinds.data ?? []).map((item) => item.kind)), [kinds.data]);
  const kindOptions = useMemo(() => {
    const known = new Set([
      ...(kinds.data ?? []).map((item) => item.kind),
      ...(jobs.data?.items ?? []).map((job) => job.kind),
    ]);
    if (kind) known.add(kind);
    return [
      { value: "all", label: "All jobs" },
      ...[...known].sort().map((value) => ({ value, label: descriptions.get(value) ?? value })),
    ];
  }, [kinds.data, jobs.data, kind, descriptions]);

  function setFilter(name: "status" | "kind", value: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value || value === "all") next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  }

  function cancelJob(job: Job) {
    cancel.mutate(job.id, {
      onSuccess: (updated) =>
        toast.info(updated.status === "cancelled" ? "Job cancelled" : "Cancel requested", {
          description: updated.status === "cancelled" ? undefined : "The job stops at its next checkpoint.",
        }),
      onError: (error) => toast.error("Couldn’t cancel the job", { description: describeError(error) }),
    });
  }

  function canRequeue(job: Job): boolean {
    return canEdit && isJobRequeueable(job) && startableKinds.has(job.kind);
  }

  function requeueJob(job: Job) {
    requeue.mutate(job.id, {
      onSuccess: () => {
        setDetailsJob(null);
        toast.success("Job queued again", { description: descriptions.get(job.kind) ?? job.kind });
      },
      onError: (error) => toast.error("Couldn’t retry the job", { description: describeError(error) }),
    });
  }

  function errorSummaryButton(job: Job, prefix: string) {
    return (
      <Tooltip content={job.last_error_summary}>
        <button
          type="button"
          className={styles.errorButton}
          data-status={job.status}
          aria-haspopup="dialog"
          onClick={() => setDetailsJob(job)}
        >
          <VisuallyHidden>Error details:</VisuallyHidden> {prefix}
          <span translate="no">{job.last_error_summary}</span>
        </button>
      </Tooltip>
    );
  }

  const columns: DataTableColumn<Job>[] = [
    {
      id: "job",
      header: "Job",
      width: "wide",
      cell: (job) => (
        <span className={styles.jobCell}>
          <span>{descriptions.get(job.kind) ?? job.kind}</span>
          <span className={styles.kind}>
            {job.kind}
            {job.schedule_name ? " (scheduled)" : ""}
          </span>
        </span>
      ),
    },
    { id: "status", header: "Status", width: "fit", cell: (job) => <JobStatusBadge job={job} /> },
    {
      id: "progress",
      header: "Progress",
      cell: (job) => {
        const { done, total, message } = job.progress ?? {};
        if (job.status === "failed" && job.last_error_summary) return errorSummaryButton(job, "");
        if (job.status === "queued" && job.last_error_summary) return errorSummaryButton(job, "Earlier attempt: ");
        if (job.status === "running" && total) {
          return (
            <span className={styles.progressCell}>
              <Progress
                label={`Progress of ${job.kind}`}
                hideLabel
                value={done ?? 0}
                max={total}
                valueText={`${done ?? 0} of ${total}`}
              />
              {message ? <span className={styles.message}>{message}</span> : null}
            </span>
          );
        }
        if (job.status === "running") {
          return (
            <span className={styles.progressCell}>
              <Progress label={`Progress of ${job.kind}`} hideLabel value={null} valueText={message || "Working…"} />
            </span>
          );
        }
        return message ? <span className={styles.message}>{message}</span> : null;
      },
    },
    {
      id: "created",
      header: "Created",
      width: "fit",
      sortable: true,
      sortValue: (job) => job.created_at,
      cell: (job) => <RelativeTime value={job.created_at} />,
    },
    {
      id: "duration",
      header: "Duration",
      numeric: true,
      width: "fit",
      cell: (job) => formatDuration(elapsed(job, now)),
    },
    {
      id: "attempts",
      header: "Attempts",
      numeric: true,
      width: "fit",
      cell: (job) => `${job.attempts} of ${job.max_attempts}`,
    },
  ];

  const startDisabledReason = !canEdit
    ? "Viewers can’t start jobs."
    : kinds.isSuccess && kinds.data.length === 0
      ? "No job kinds can be started from the app yet."
      : null;
  const filtersActive = status !== undefined || kind !== undefined;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Jobs"
        description="Imports, supplier syncs, crawls and backups run in the background worker. Progress updates here while a job runs."
        meta={
          worker.data ? (
            worker.data.paused ? (
              <Badge tone="warning" icon="pause">
                Worker paused
              </Badge>
            ) : worker.data.online ? (
              <Badge tone="success" icon="checkCircle">
                Worker running
              </Badge>
            ) : (
              <Badge tone="warning" icon="alertTriangle">
                Worker not running
              </Badge>
            )
          ) : null
        }
        actions={
          <Tooltip content={startDisabledReason}>
            <Button
              variant="primary"
              icon="play"
              onClick={() => setEnqueueOpen(true)}
              disabled={startDisabledReason !== null}
              focusableWhenDisabled
            >
              Start job…
            </Button>
          </Tooltip>
        }
      />

      {worker.data?.paused ? (
        <InlineAlert tone="warning" title="The background worker is paused">
          Queued jobs and schedules wait until WORKER_PAUSED is turned off. It’s set while a backup is restored on this
          server.
        </InlineAlert>
      ) : worker.data && !worker.data.online ? (
        <InlineAlert tone="warning" title="The background worker isn’t running">
          Queued jobs wait until it starts.
          {worker.data.last_seen_at ? ` It was last seen ${formatRelativeTime(worker.data.last_seen_at)}.` : ""} With
          Docker, check the worker service (docker compose logs worker).
        </InlineAlert>
      ) : null}

      <Toolbar label="Filter jobs">
        <Select
          label="Status"
          size="sm"
          options={[
            { value: "all", label: "All statuses" },
            ...JOB_STATUSES.map((value) => ({ value, label: STATUS_LOOK[value].label })),
          ]}
          value={status ?? "all"}
          onValueChange={(value) => setFilter("status", value)}
        />
        <Select
          label="Job"
          size="sm"
          options={kindOptions}
          value={kind ?? "all"}
          onValueChange={(value) => setFilter("kind", value)}
        />
      </Toolbar>

      <DataTable
        caption="Recent jobs"
        columns={columns}
        rows={jobs.data?.items}
        getRowId={(job) => job.id}
        loading={jobs.isPending}
        error={jobs.error}
        onRetry={() => void jobs.refetch()}
        defaultSort={{ columnId: "created", direction: "desc" }}
        rowActionsLabel={(job) => `Actions for ${descriptions.get(job.kind) ?? job.kind}`}
        rowActions={(job) => [
          { label: "Details…", icon: "info", onSelect: setDetailsJob },
          ...(canRequeue(job) ? [{ label: "Retry job", icon: "refresh" as const, onSelect: requeueJob }] : []),
          ...(canEdit && isJobActive(job) && !job.cancel_requested
            ? [{ label: "Cancel job", icon: "stop" as const, danger: true, onSelect: cancelJob }]
            : []),
        ]}
        empty={
          filtersActive ? (
            <EmptyState
              variant="inline"
              headingLevel={3}
              title="No jobs match these filters"
              actions={<Button onClick={() => setSearchParams({}, { replace: true })}>Clear filters</Button>}
            />
          ) : (
            <EmptyState
              variant="inline"
              headingLevel={3}
              icon="list"
              title="No jobs yet"
              description="Jobs appear here when you start an import or sync, and when scheduled work runs overnight."
            />
          )
        }
      />
      {jobs.data && jobs.data.count > jobs.data.items.length ? (
        <p className={styles.footnote}>
          Showing the {jobs.data.items.length} most recent of {jobs.data.count} jobs.
        </p>
      ) : null}

      <EnqueueJobDialog open={enqueueOpen} onOpenChange={setEnqueueOpen} kinds={kinds.data ?? []} />
      <JobDetailsDialog
        job={detailsJob}
        description={detailsJob ? descriptions.get(detailsJob.kind) : undefined}
        onClose={() => setDetailsJob(null)}
        onRequeue={detailsJob && canRequeue(detailsJob) ? requeueJob : undefined}
        requeuePending={requeue.isPending}
      />
    </div>
  );
}
